import {
  ArchiveOutlined,
  DeleteOutline,
  EditOutlined,
  ImageOutlined,
  Inventory2Outlined,
  MoreHoriz,
  PaidOutlined,
  PublishOutlined,
  TuneOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
} from "@mui/material";
import { MouseEvent, ReactNode, useState } from "react";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { userStore } from "../../../../../stores/sharedStores";
import { SwayyEmployeeRoles } from "../../../../../models/Employees/SwayyEmployeeRoles";
import {
  SwayyProduct,
  SwayyProductAction,
} from "../../../models/products/SwayyProduct";
import { swayyColors, swayyRadii } from "../../../theme/tokens";

interface SwayyProductActionMenuProps {
  product: SwayyProduct;
  onAction: (product: SwayyProduct, action: SwayyProductAction) => void;
}

const EDIT_ACTIONS: SwayyProductAction[] = [
  "edit",
  "variants",
  "images",
  "price-inventory",
  "publish",
  "archive",
  "delete",
];

interface ProductMenuAction {
  id: SwayyProductAction;
  label: string;
  icon: ReactNode;
  danger?: boolean;
}

const SwayyProductActionMenu = ({
  product,
  onAction,
}: SwayyProductActionMenuProps) => {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const canEditCatalog = userStore.hasSwayyPermission(
    SwayyEmployeeRoles.EDIT_CATALOG,
  );

  const lifecycleAction: ProductMenuAction =
    product.status === "draft"
      ? { id: "publish", label: "Publish product", icon: <PublishOutlined /> }
      : product.status === "live"
        ? {
            id: "archive",
            label: "Archive product",
            icon: <ArchiveOutlined />,
          }
        : {
            id: "publish",
            label: "Publish again",
            icon: <PublishOutlined />,
          };

  const allActions = [
    { id: "view", label: "View details", icon: <VisibilityOutlined /> },
    { id: "edit", label: "Edit product", icon: <EditOutlined /> },
    { id: "variants", label: "Manage variants", icon: <TuneOutlined /> },
    { id: "images", label: "Manage images", icon: <ImageOutlined /> },
    {
      id: "price-inventory",
      label: "Manage price and inventory",
      icon: <PaidOutlined />,
    },
    lifecycleAction,
  ] satisfies ProductMenuAction[];

  const actions = allActions.filter(
    (action) => canEditCatalog || !EDIT_ACTIONS.includes(action.id),
  );

  const openMenu = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorElement(event.currentTarget);
  };

  const closeMenu = (event?: {}) => {
    setAnchorElement(null);
  };

  const selectAction = (action: SwayyProductAction) => {
    setAnchorElement(null);
    onAction(product, action);
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={`Open actions for ${product.name}`}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorElement)}
        onClick={openMenu}
        sx={{
          color: swayyColors.deepGreen,
          border: `1px solid ${swayyColors.border}`,
          borderRadius: swayyRadii.sm,
          "&:hover": { backgroundColor: swayyColors.pineLight },
        }}
      >
        <MoreHoriz fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorElement}
        open={Boolean(anchorElement)}
        onClose={closeMenu}
        onClick={(event) => event.stopPropagation()}
        slotProps={{ paper: { sx: { minWidth: 240 } } }}
      >
        {actions.map((action) => (
          <MenuItem key={action.id} onClick={() => selectAction(action.id)}>
            <ListItemIcon
              sx={{
                color: swayyColors.textSecondary,
                "& .MuiSvgIcon-root": { fontSize: 18 },
              }}
            >
              {action.icon}
            </ListItemIcon>
            <CustomTypography variant="body2">{action.label}</CustomTypography>
          </MenuItem>
        ))}
        {canEditCatalog && (
          <>
            <Divider />
            <MenuItem onClick={() => selectAction("delete")}>
              <ListItemIcon sx={{ color: swayyColors.danger }}>
                <DeleteOutline fontSize="small" />
              </ListItemIcon>
              <CustomTypography variant="body2" color={swayyColors.danger}>
                Delete product
              </CustomTypography>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export default SwayyProductActionMenu;
